<script setup lang="ts">
  import { computed, ref, watch, nextTick, onMounted } from 'vue';
  import type { FormElement, HeaderProperties } from '../../../model/form.types';
  import { useElementContainerStyles } from '../../../model/useElementContainerStyles';
  import { useFormBuilderStore } from '../../../model/form-builder.store';

  interface Props {
    element: FormElement & { properties: HeaderProperties };
  }

  const props = defineProps<Props>();

  const formBuilderStore = useFormBuilderStore();
  const headerRef = ref<HTMLElement | null>(null);
  const isEditing = ref(false);

  const textAlignClass = computed(() => {
    const alignments: Record<string, string> = {
      left: 'text-left',
      center: 'text-center',
      right: 'text-right',
      justify: 'text-justify',
    };
    return (
      alignments[(props.element.properties.textAlign as keyof typeof alignments) || 'left'] ||
      'text-left'
    );
  });

  const fontWeightClass = computed(() => {
    const weights = {
      normal: 'font-normal',
      semibold: 'font-semibold',
      bold: 'font-bold',
    };
    return (
      weights[(props.element.properties.fontWeight as keyof typeof weights) || 'normal'] ||
      'font-semibold'
    );
  });

  const headerComponent = computed(() => {
    const level = props.element.properties.level || 2;
    const validLevels = [1, 2, 3] as const;
    const safeLevel = validLevels.includes(level as 1 | 2 | 3) ? level : 2;
    return `h${safeLevel}`;
  });

  const { containerStyles: elementStyles } = useElementContainerStyles(
    computed(() => props.element.properties)
  );

  const handleClick = (event: MouseEvent) => {
    event.stopPropagation();
    if (!isEditing.value) {
      isEditing.value = true;
      nextTick(() => {
        if (headerRef.value) {
          headerRef.value.focus();
          const range = document.createRange();
          range.selectNodeContents(headerRef.value);
          range.collapse(false);
          const selection = window.getSelection();
          selection?.removeAllRanges();
          selection?.addRange(range);
        }
      });
    }
  };

  const handleBlur = () => {
    isEditing.value = false;
    if (headerRef.value) {
      const newText = headerRef.value.textContent || '';
      if (newText !== props.element.properties.label) {
        formBuilderStore.updateElement(props.element.id, {
          properties: {
            ...props.element.properties,
            label: newText,
          },
        });
      }
    }
  };

  const handleKeyDown = (event: KeyboardEvent) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      headerRef.value?.blur();
    }
    if (event.key === 'Escape') {
      if (headerRef.value) {
        headerRef.value.textContent = props.element.properties.label || '';
        headerRef.value.blur();
      }
    }
  };

  watch(
    () => props.element.properties.label,
    () => {
      if (headerRef.value && !isEditing.value) {
        headerRef.value.textContent = props.element.properties.label || '';
      }
    },
    { immediate: true }
  );

  onMounted(() => {
    if (headerRef.value) {
      headerRef.value.textContent = props.element.properties.label || '';
    }
  });
</script>

<template>
  <component
    :is="headerComponent"
    ref="headerRef"
    :class="['header-element', textAlignClass, fontWeightClass]"
    :style="elementStyles"
    :contenteditable="isEditing"
    @click="handleClick"
    @blur="handleBlur"
    @keydown="handleKeyDown"
  />
</template>

<style scoped>
  .header-element {
    font-family: var(--font-base);
    cursor: text;
    outline: none;
    margin: 0;
    line-height: 1.3;
  }

  .header-element[contenteditable='true'] {
    cursor: text;
  }

  .header-element[contenteditable='false'] {
    cursor: pointer;
  }

  .header-element:focus {
    outline: none;
  }
</style>
